import { useState, useEffect, useMemo, useRef, useCallback } from "react"
import { useActionCable } from "~/react/hooks/useActionCable"
import {
  DataTable,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableBody,
  DataTableRow,
  DataTableCell,
  DataTableCellActions,
  DataTableCheckboxCell,
  DataTableGroupRow,
  DataTableEmptyState,
} from "~/react/ui/data-table"
import { DataTableBulkActionsBar } from "~/react/components/data-table-bulk-actions-bar"
import { DataTableSkeleton } from "~/react/components/data-table/data-table-skeleton"
import { Alert, AlertIcon, AlertContent, AlertDescription } from "~/react/ui/alert"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "~/react/ui/tooltip"
import { Tag, TagLabel } from "~/react/ui/tag"
import { getDomainStatusInfo } from "~/react/components/email_accounts/domain_status"
import { HealthScoreIndicator } from "~/react/ui/health-score-indicator"
import { ActionsCell } from "~/react/components/data-table/cells/actions-cell"
import { useTableSelection } from "~/react/hooks/useTableSelection"
import { Skeleton } from "~/react/ui/skeleton"
import { Checkbox } from "~/react/ui/checkbox"
import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import {
  FiltersDropdown,
  FiltersTags,
  filterDomains,
  type DomainFilters,
  type DomainData,
  type EmailAccountOption,
} from "~/react/components/email_accounts/domains_filters"
import { SearchInput } from "~/react/ui/search-input"
import DomainSidepanel from "~/react/components/email_accounts/domain_sidepanel"
import { Sheet, SheetContent } from "~/react/ui/sheet"
// @ts-expect-error - @hotwired/turbo-rails doesn't have TypeScript declarations
import { Turbo } from "@hotwired/turbo-rails"
import { getCsrfToken, formatDate } from "~/lib/utils"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogCloseButton,
  DialogBody,
  DialogFooter,
} from "~/react/ui/dialog"

import {
  email_accounts_domains_path,
  email_domains_path,
  email_domains_health_checks_path,
  email_domains_bulk_subscription_path,
} from "~/lib/routes"

// Import provider icons to ensure proper asset resolution in production
import googleIcon from "~/images/connected_apps/google_icon.png"
import microsoftIcon from "~/images/connected_apps/microsoft_icon.png"
import smtpImapIcon from "~/images/connected_apps/smtp_imap_at_icon.svg"
import hubspotIcon from "~/images/connected_apps/hubspot_icon.png"
import zapierIcon from "~/images/connected_apps/zapier_icon.png"
import makeIcon from "~/images/connected_apps/make_icon.png"
import clayIcon from "~/images/connected_apps/clay_icon.png"
import zohoIcon from "~/images/connected_apps/zoho_icon.png"
import hunterIcon from "~/images/connected_apps/hunter_icon.png"
import closeIcon from "~/images/connected_apps/close_icon.png"
import copperIcon from "~/images/connected_apps/copper_icon.png"

type DomainDataWithId = DomainData & { id: string }

type SectionedDomainData = {
  connected: DomainDataWithId[]
  managed: DomainDataWithId[]
  total: number
}

type Props = {
  total: number
  emailAccounts?: EmailAccountOption[]
  allowEmailProvisioning?: boolean
  isPremium?: boolean
  initialDomainId?: string | null
  onDomainIdChange?: (domainId: string | null) => void
}

const getFiltersFromLocation = (): DomainFilters => {
  if (typeof window === "undefined") {
    return {
      emailAccountIds: [],
      status: [],
      healthFrom: null,
      healthTo: null,
      healthIsEmpty: false,
      healthHasAnyValue: false,
      type: [],
      searchQuery: "",
    }
  }

  const params = new URLSearchParams(window.location.search)

  // Parse email account IDs array
  const emailAccountIdsParam = params.get("domain_email_account_ids")
  const emailAccountIds = emailAccountIdsParam
    ? emailAccountIdsParam
        .split("|")
        .map((id) => parseInt(id, 10))
        .filter((id) => !isNaN(id))
    : []

  // Parse status array
  const statusParam = params.get("domain_status")
  const status = statusParam ? statusParam.split("|") : []

  // Parse health range
  const healthFromParam = params.get("domain_health_from")
  const parsedHealthFrom = healthFromParam ? parseInt(healthFromParam, 10) : null
  const healthFrom = parsedHealthFrom !== null && Number.isNaN(parsedHealthFrom) ? null : parsedHealthFrom
  const healthToParam = params.get("domain_health_to")
  const parsedHealthTo = healthToParam ? parseInt(healthToParam, 10) : null
  const healthTo = parsedHealthTo !== null && Number.isNaN(parsedHealthTo) ? null : parsedHealthTo
  const healthIsEmpty = params.get("domain_health_empty") === "true"
  const healthHasAnyValue = params.get("domain_health_has_any_value") === "true"

  // Parse type array
  const typeParam = params.get("domain_type")
  const type = typeParam ? typeParam.split("|") : []

  // Parse search query
  const searchQuery = params.get("domain_query") || ""

  return {
    emailAccountIds,
    status,
    healthFrom,
    healthTo,
    healthIsEmpty,
    healthHasAnyValue,
    type,
    searchQuery,
  }
}

const updateFiltersInURL = (filters: DomainFilters) => {
  if (typeof window === "undefined") return

  const params = new URLSearchParams(window.location.search)

  // Update email account IDs
  if (filters.emailAccountIds.length > 0) {
    params.set("domain_email_account_ids", filters.emailAccountIds.join("|"))
  } else {
    params.delete("domain_email_account_ids")
  }

  // Update status
  if (filters.status.length > 0) {
    params.set("domain_status", filters.status.join("|"))
  } else {
    params.delete("domain_status")
  }

  // Update health range
  if (filters.healthFrom !== null) {
    params.set("domain_health_from", filters.healthFrom.toString())
  } else {
    params.delete("domain_health_from")
  }

  if (filters.healthTo !== null) {
    params.set("domain_health_to", filters.healthTo.toString())
  } else {
    params.delete("domain_health_to")
  }

  if (filters.healthIsEmpty) {
    params.set("domain_health_empty", "true")
  } else {
    params.delete("domain_health_empty")
  }

  if (filters.healthHasAnyValue) {
    params.set("domain_health_has_any_value", "true")
  } else {
    params.delete("domain_health_has_any_value")
  }

  // Update type
  if (filters.type.length > 0) {
    params.set("domain_type", filters.type.join("|"))
  } else {
    params.delete("domain_type")
  }

  // Update search query
  if (filters.searchQuery) {
    params.set("domain_query", filters.searchQuery)
  } else {
    params.delete("domain_query")
  }

  // Update URL without reload
  const queryString = params.toString()
  const newURL = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname
  window.history.replaceState(null, "", newURL)
}

// Map of provider icons using imported assets for proper production builds
const providerIconMap: Record<string, string> = {
  gmail: googleIcon,
  outlook: microsoftIcon,
  smtp_imap: smtpImapIcon,
  hubspot: hubspotIcon,
  zapier: zapierIcon,
  make: makeIcon,
  clay: clayIcon,
  zoho: zohoIcon,
  hunter: hunterIcon,
  close: closeIcon,
  copper: copperIcon,
}

function getProviderInfo(provider: string | null): { label: string; iconImage: string } | null {
  if (!provider) return null

  const providerLabels: Record<string, string> = {
    gmail: i18n.t("js.email_accounts_center.domains_table.providers.gmail"),
    outlook: i18n.t("js.email_accounts_center.domains_table.providers.outlook"),
    smtp_imap: i18n.t("js.email_accounts_center.domains_table.providers.smtp_imap"),
    hubspot: i18n.t("js.email_accounts_center.domains_table.providers.hubspot"),
    zapier: i18n.t("js.email_accounts_center.domains_table.providers.zapier"),
    make: i18n.t("js.email_accounts_center.domains_table.providers.make"),
    clay: i18n.t("js.email_accounts_center.domains_table.providers.clay"),
    zoho: i18n.t("js.email_accounts_center.domains_table.providers.zoho"),
    hunter: i18n.t("js.email_accounts_center.domains_table.providers.hunter"),
    close: i18n.t("js.email_accounts_center.domains_table.providers.close"),
    copper: i18n.t("js.email_accounts_center.domains_table.providers.copper"),
  }

  const iconImage = providerIconMap[provider]
  if (iconImage) {
    return {
      label: providerLabels[provider] || provider.charAt(0).toUpperCase() + provider.slice(1),
      iconImage,
    }
  }

  // Fallback for unknown providers - use smtp_imap icon as default
  return {
    label: provider.charAt(0).toUpperCase() + provider.slice(1),
    iconImage: smtpImapIcon,
  }
}

const COLUMN_COUNT = 7 // checkbox + 6 data columns

export default function DomainsTable(props: Props) {
  const {
    total,
    emailAccounts,
    allowEmailProvisioning = false,
    isPremium = false,
    initialDomainId,
    onDomainIdChange,
  } = props
  const [rawData, setRawData] = useState<DomainData[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<DomainFilters>(() => getFiltersFromLocation())
  const [activeDomain, setActiveDomain] = useState<DomainData | null>(null)
  const didMountUrlSyncRef = useRef(false)
  const [isPolling, setIsPolling] = useState(false)
  const [confirmBulkCancelRenewalOpen, setConfirmBulkCancelRenewalOpen] = useState(false)
  const [confirmIndividualCancelRenewalOpen, setConfirmIndividualCancelRenewalOpen] = useState(false)
  const [pendingDomain, setPendingDomain] = useState<DomainData | null>(null)
  const bulkCancelRenewalDialogRef = useRef<HTMLDialogElement>(null)
  const individualCancelRenewalDialogRef = useRef<HTMLDialogElement>(null)
  const pollingRef = useRef<{
    domain: string
    initialScore: number | null
    pollCount: number
  } | null>(null)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      const response = await fetch(email_domains_path({ format: "json" }), {
        headers: {
          Accept: "application/json",
        },
        credentials: "same-origin",
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const json = await response.json()
      setRawData(
        (json as Array<{ score?: number | null; health?: number | null } & DomainData>).map(
          (d) => ({ ...d, score: d.score ?? d.health ?? null }) as DomainData,
        ),
      )
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : i18n.t("js.email_accounts_center.domains_table.errors.load_failed"))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetchData()
  }, [fetchData])

  // Sync filters to URL whenever they change
  useEffect(() => {
    updateFiltersInURL(filters)
  }, [filters])

  // Open sidepanel if initialDomainId is provided
  useEffect(() => {
    // If URL no longer has a domain_id, close the sidepanel
    if (!initialDomainId) {
      setActiveDomain(null)
      return
    }

    // Prefer rawData so deep-links work even if filters hide the domain
    if (rawData) {
      const domain = rawData.find((d) => d.domain === initialDomainId)
      if (domain) {
        setActiveDomain(domain)
      }
    }
  }, [initialDomainId, rawData])

  // Update URL when activeDomain changes
  useEffect(() => {
    if (onDomainIdChange) {
      // Don't clear `domain_id` on first mount; it would erase deep-links before data loads.
      if (!didMountUrlSyncRef.current) {
        didMountUrlSyncRef.current = true
        if (!activeDomain) return
      }

      onDomainIdChange(activeDomain?.domain ?? null)
    }
  }, [activeDomain, onDomainIdChange])

  // Filter domains based on filter state and search query
  const filteredData = useMemo(() => {
    return filterDomains(rawData, filters, emailAccounts)
  }, [rawData, filters, emailAccounts])

  const displayData = filteredData ?? rawData

  // Transform filtered data to sectioned format for the selection hook
  const sectionedData = useMemo<SectionedDomainData | null>(() => {
    if (!displayData) return null

    const connected: DomainDataWithId[] = displayData
      .filter((d) => d.type === "connected")
      .map((d) => ({ ...d, id: d.domain }))
    const managed: DomainDataWithId[] = displayData
      .filter((d) => d.type === "managed")
      .map((d) => ({ ...d, id: d.domain }))

    return {
      connected,
      managed,
      total: displayData.length,
    }
  }, [displayData])

  const {
    selectedIds,
    selectedCount,
    toggleSelection,
    clearSelection,
    selectAll,
    allItems,
    connectedItems,
    managedItems,
    isSelected,
  } = useTableSelection(sectionedData)

  // Helper to filter out pending domains
  const getNonPendingItems = useCallback((items: DomainDataWithId[]) => {
    return items.filter((item) => item.status !== "pending")
  }, [])

  // Get all non-pending items
  const nonPendingItems = useMemo(() => {
    return getNonPendingItems(allItems)
  }, [allItems, getNonPendingItems])

  // Check if all non-pending items are selected
  const isAllNonPendingSelected = useMemo(() => {
    if (nonPendingItems.length === 0) return false
    return nonPendingItems.every((item) => selectedIds.includes(item.domain))
  }, [nonPendingItems, selectedIds])

  // Check if all non-pending connected items are selected
  const isConnectedSectionNonPendingSelected = useMemo(() => {
    const nonPendingConnected = getNonPendingItems(connectedItems)
    if (nonPendingConnected.length === 0) return false
    return nonPendingConnected.every((item) => selectedIds.includes(item.domain))
  }, [connectedItems, selectedIds, getNonPendingItems])

  // Check if all non-pending managed items are selected
  const isManagedSectionNonPendingSelected = useMemo(() => {
    const nonPendingManaged = getNonPendingItems(managedItems)
    if (nonPendingManaged.length === 0) return false
    return nonPendingManaged.every((item) => selectedIds.includes(item.domain))
  }, [managedItems, selectedIds, getNonPendingItems])

  // Select only non-pending items in a section
  const selectNonPendingSection = useCallback(
    (section: "connected" | "managed") => {
      const sectionItems = section === "connected" ? connectedItems : managedItems
      const nonPendingSectionItems = getNonPendingItems(sectionItems)

      // If all are selected, deselect all; otherwise select all
      const allSelected = nonPendingSectionItems.every((item) => selectedIds.includes(item.domain))
      if (allSelected) {
        // Deselect all non-pending items in this section
        // Keep all other selected items (from other sections or pending items)
        const itemsToKeep = allItems.filter(
          (item) =>
            selectedIds.includes(item.domain) && !nonPendingSectionItems.some((np) => np.domain === item.domain),
        )
        selectAll(itemsToKeep)
      } else {
        // Select all non-pending items in this section
        // Keep all currently selected items and add non-pending items from this section
        const currentlySelectedItems = allItems.filter((item) => selectedIds.includes(item.domain))
        const newSelectedItems = [
          ...currentlySelectedItems,
          ...nonPendingSectionItems.filter((item) => !selectedIds.includes(item.domain)),
        ]
        selectAll(newSelectedItems)
      }
    },
    [connectedItems, managedItems, selectedIds, allItems, selectAll, getNonPendingItems],
  )

  const handleRunHealthCheck = useCallback(async () => {
    // Filter out pending domains
    const nonPendingSelectedIds = selectedIds.filter((id) => {
      const domain = allItems.find((d) => d.domain === id)
      return domain && domain.status !== "pending"
    })

    if (nonPendingSelectedIds.length === 0) return

    try {
      const response = await fetch(email_domains_health_checks_path(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify({
          domain: nonPendingSelectedIds,
        }),
      })

      if (response.ok) {
        clearSelection()
        // Update local state to show pending status immediately for all selected domains
        setRawData((prev) =>
          prev
            ? prev.map((d) => (nonPendingSelectedIds.includes(d.domain) ? { ...d, health_check_status: "pending" } : d))
            : prev,
        )
        setActiveDomain((prev) =>
          prev && nonPendingSelectedIds.includes(prev.domain) ? { ...prev, health_check_status: "pending" } : prev,
        )
      } else {
        const error = await response.json()
        App?.displayFlash?.("error", error.error || i18n.t("js.email_accounts_center.bulk.health_check_error"))
      }
    } catch (err) {
      App?.displayFlash?.("error", i18n.t("js.email_accounts_center.bulk.health_check_error"))
    }
  }, [selectedIds, allItems, clearSelection])

  const handleIndividualHealthCheck = async (domain: DomainData) => {
    try {
      const response = await fetch(email_domains_health_checks_path(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify({
          domain: [domain.domain],
        }),
      })

      if (response.ok) {
        // Update local state to show pending status immediately
        setRawData((prev) =>
          prev ? prev.map((d) => (d.domain === domain.domain ? { ...d, health_check_status: "pending" } : d)) : prev,
        )
        setActiveDomain((prev) =>
          prev && prev.domain === domain.domain ? { ...prev, health_check_status: "pending" } : prev,
        )
      } else {
        const error = await response.json()
        App?.displayFlash?.("error", error.error || i18n.t("js.email_accounts_center.bulk.health_check_error"))
      }
    } catch (err) {
      App?.displayFlash?.("error", i18n.t("js.email_accounts_center.bulk.health_check_error"))
    }
  }

  // Handle dialog open/close
  useEffect(() => {
    if (confirmBulkCancelRenewalOpen) {
      bulkCancelRenewalDialogRef.current?.showModal()
    } else {
      bulkCancelRenewalDialogRef.current?.close()
    }
  }, [confirmBulkCancelRenewalOpen])

  useEffect(() => {
    if (confirmIndividualCancelRenewalOpen) {
      individualCancelRenewalDialogRef.current?.showModal()
    } else {
      individualCancelRenewalDialogRef.current?.close()
    }
  }, [confirmIndividualCancelRenewalOpen])

  const handleCancelRenewal = useCallback(() => {
    if (selectedIds.length === 0) return
    setConfirmBulkCancelRenewalOpen(true)
  }, [selectedIds])

  const executeBulkCancelRenewal = useCallback(async () => {
    // Filter out pending domains
    const nonPendingSelectedIds = selectedIds.filter((id) => {
      const domain = allItems.find((d) => d.domain === id)
      return domain && domain.status !== "pending"
    })

    if (nonPendingSelectedIds.length === 0) return

    try {
      const response = await fetch(email_domains_bulk_subscription_path(), {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify({
          domains: nonPendingSelectedIds,
        }),
      })

      if (response.status === 202) {
        App?.displayFlash(
          "success",
          i18n.t("js.email_accounts_center.domains_table.bulk.cancel_renewal_queued", {
            count: nonPendingSelectedIds.length,
          }),
        )
        clearSelection()
      } else if (response.ok) {
        const data = await response.json().catch(() => ({ cancelled_count: 0 }))
        const cancelledCount = typeof data?.cancelled_count === "number" ? data.cancelled_count : 0

        App.displayFlash(
          "success",
          i18n.t("js.email_accounts_center.domains_table.bulk.cancel_renewal_success", {
            count: cancelledCount || nonPendingSelectedIds.length,
          }),
        )
        clearSelection()
      } else {
        const error = await response.json().catch(() => ({ error: i18n.t("js.unknown_error") }))
        App?.displayFlash?.("error", error.error || i18n.t("js.email_accounts_center.bulk.cancel_renewal_error"))
      }
    } catch (err) {
      App?.displayFlash?.("error", i18n.t("js.email_accounts_center.bulk.cancel_renewal_error"))
    }
  }, [selectedIds, allItems, clearSelection])

  const handleIndividualCancelRenewal = (domain: DomainData) => {
    if (domain.type !== "managed") return
    setPendingDomain(domain)
    setConfirmIndividualCancelRenewalOpen(true)
  }

  const executeIndividualCancelRenewal = async (domain: DomainData) => {
    try {
      const response = await fetch(`/email-domains/${encodeURIComponent(domain.domain)}/subscription`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
      })

      if (response.ok) {
        let mailboxCount = 0

        const data = await response.json()
        mailboxCount = data.mailbox_count || 0

        App?.displayFlash(
          "success",
          i18n.t("js.email_accounts_center.domain.cancel_renewal_success", {
            count: mailboxCount,
          }),
        )
      } else {
        const error = await response.json().catch(() => ({ error: i18n.t("js.unknown_error") }))
        App?.displayFlash?.("error", error.error || i18n.t("js.email_accounts_center.bulk.cancel_renewal_error"))
      }
    } catch (err) {
      App?.displayFlash?.("error", i18n.t("js.email_accounts_center.bulk.cancel_renewal_error"))
    }
  }

  // Compute bulk actions based on selected domains
  const bulkActions = useMemo(() => {
    // Check if any selected domain has a pending health check
    const hasPendingHealthCheck =
      selectedCount > 0 &&
      allItems
        .filter((domain) => selectedIds.includes(domain.domain))
        .some((domain) => domain.health_check_status === "pending")

    // Filter out pending domains from selected
    const nonPendingSelectedDomains = allItems.filter(
      (domain) => selectedIds.includes(domain.domain) && domain.status !== "pending",
    )

    const actions = [
      {
        label: i18n.t("js.email_accounts_center.bulk.run_health_check"),
        icon: "fa-heart-pulse",
        onClick: () => {
          void handleRunHealthCheck()
        },
        disabled: selectedCount === 0 || hasPendingHealthCheck || nonPendingSelectedDomains.length === 0,
      },
    ]

    // Only include "Cancel renewal" if all selected domains are managed and non-pending
    if (nonPendingSelectedDomains.length > 0) {
      const allAreManaged = nonPendingSelectedDomains.every((domain) => domain.type === "managed")

      if (allAreManaged) {
        actions.push({
          label: i18n.t("js.email_accounts_center.actions.cancel_renewal"),
          icon: "fa-times-circle",
          onClick: () => {
            void handleCancelRenewal()
          },
          disabled: nonPendingSelectedDomains.length === 0,
        })
      }
    }

    return actions
  }, [selectedIds, allItems, handleRunHealthCheck, handleCancelRenewal])

  const renderDomainRow = (domain: DomainData, section?: string) => {
    const statusInfo = getDomainStatusInfo(domain.status)
    const isPending = domain.status === "pending"
    return (
      <DataTableRow key={domain.domain} id={domain.domain}>
        <DataTableCheckboxCell
          aria-label={i18n.t("js.data_table.select_record", {
            record: domain.domain,
          })}
          recordId={domain.domain}
          section={section}
          checked={isSelected(domain.domain)}
          onChange={() => toggleSelection(domain.domain)}
          disabled={isPending}
        />
        <DataTableCell sticky>
          <Button
            variant="ghost"
            size="xs"
            className="lead-tag lead-tag--people"
            type="button"
            onClick={() => setActiveDomain(domain)}
          >
            <span className="lead-tag__name">{domain.domain}</span>
          </Button>
        </DataTableCell>
        <DataTableCell>
          <Tag variant={statusInfo.variant}>
            <TagLabel>{statusInfo.label}</TagLabel>
          </Tag>
        </DataTableCell>
        <DataTableCell>
          {domain.email_accounts_count > 0 ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <a
                    className="h-data-table-cell__link"
                    href={`/campaigns/email-accounts-center?query=${encodeURIComponent(domain.domain)}`}
                  >
                    {i18n.t("js.email_accounts_center.domains_table.email_account", {
                      count: domain.email_accounts_count,
                    })}
                  </a>
                </TooltipTrigger>
                {domain.email_accounts && domain.email_accounts.length > 0 && (
                  <TooltipContent>
                    <div className="d-flex flex-column gap-1">
                      {domain.email_accounts.map((email) => (
                        <span key={email}>{email}</span>
                      ))}
                    </div>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          ) : (
            <span className="text-xs text-body-secondary">
              {i18n.t("js.email_accounts_center.domains_table.email_account.other", {
                count: 0,
              })}
            </span>
          )}
        </DataTableCell>
        {isPremium && (
          <DataTableCell style={{ width: "18rem", minWidth: "18rem" }}>
            <HealthScoreIndicator score={domain.score ?? null} loading={domain.health_check_status === "pending"} />
            {domain.score != null && (
              <DataTableCellActions>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="xs"
                        type="button"
                        iconOnly
                        onClick={() => handleIndividualHealthCheck(domain)}
                        disabled={domain.health_check_status === "pending" || isPending}
                        aria-label={i18n.t("js.email_accounts_center.domains_table.actions.run_health_check")}
                      >
                        <Icon icon="heart-pulse" size="sm" aria-hidden="true" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {i18n.t("js.email_accounts_center.domains_table.actions.run_health_check")}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </DataTableCellActions>
            )}
          </DataTableCell>
        )}
        <DataTableCell>
          {(() => {
            const providerInfo = getProviderInfo(domain.provider)
            if (providerInfo) {
              return (
                <>
                  <img src={providerInfo.iconImage} alt="" className="p-05" width={16} height={16} />
                  {providerInfo.label}
                </>
              )
            }
            // Fallback to type if no provider
            return (
              <>
                <img src={smtpImapIcon} alt="" className="p-05" width={16} height={16} />
                {i18n.t("js.email_accounts_center.domains_table.providers.smtp_imap")}
              </>
            )
          })()}
        </DataTableCell>
        <DataTableCell>
          <ActionsCell
            primaryAction={{
              label: i18n.t("js.email_accounts_center.actions.view"),
              onClick: () => {
                setActiveDomain(domain)
              },
            }}
            moreActions={
              domain.type === "managed"
                ? [
                    {
                      label: i18n.t("js.email_accounts_center.actions.cancel_renewal"),
                      onClick: () => handleIndividualCancelRenewal(domain),
                      variant: "danger" as const,
                      disabled: isPending,
                    },
                    {
                      label: i18n.t("js.email_accounts_center.actions.run_health_check"),
                      onClick: () => handleIndividualHealthCheck(domain),
                      disabled: domain.health_check_status === "pending" || isPending,
                    },
                  ]
                : []
            }
            moreActionsAriaLabel={i18n.t("js.email_accounts_center.actions.more_actions_aria")}
          />
        </DataTableCell>
      </DataTableRow>
    )
  }

  // Poll the domain until health changes or max attempts reached (fallback when no push arrives)
  useEffect(() => {
    if (!isPolling || !pollingRef.current) return

    // Allow up to ~60s of polling (20 * 3s) to match email accounts behavior
    const maxPolls = 20
    const pollInterval = 3000

    const pollForHealthUpdate = async () => {
      if (!pollingRef.current) return
      const { domain, initialScore } = pollingRef.current

      try {
        const response = await fetch(`/email-domains/${encodeURIComponent(domain)}`, {
          headers: {
            Accept: "application/json",
          },
          credentials: "same-origin",
        })

        if (response.ok) {
          const updatedDomain: DomainData = await response.json()

          setRawData((prev) => (prev ? prev.map((d) => (d.domain === updatedDomain.domain ? updatedDomain : d)) : prev))

          // Update the sidepanel if it is showing this domain
          setActiveDomain((prev) => (prev && prev.domain === updatedDomain.domain ? updatedDomain : prev))

          const hasScoreChanged =
            updatedDomain.score !== undefined && updatedDomain.score !== null && updatedDomain.score !== initialScore

          if (hasScoreChanged) {
            setIsPolling(false)
            pollingRef.current = null
            return
          }
        }
      } catch (err) {
        console.error("Failed to poll domain health:", err)
      }

      if (!pollingRef.current) return
      pollingRef.current.pollCount += 1
      if (pollingRef.current.pollCount >= maxPolls) {
        setIsPolling(false)
        pollingRef.current = null
      }
    }

    const intervalId = setInterval(pollForHealthUpdate, pollInterval)
    const timeoutId = setTimeout(pollForHealthUpdate, 1000)

    return () => {
      clearInterval(intervalId)
      clearTimeout(timeoutId)
    }
  }, [isPolling])

  const healthCheckChannelParams = useMemo(() => ({ channel: "HealthChecksChannel" }), [])

  const handleHealthCheckReceived = useCallback(
    (data: { domain: string; score: number | null; report_status: string | null }) => {
      const { domain, score, report_status } = data
      const healthCheckStatus = report_status as "pending" | "completed" | null

      setRawData((prev) =>
        prev
          ? prev.map((d) => {
              if (d.domain !== domain) return d
              // Don't revert from completed (null) back to pending
              if (d.health_check_status === null && healthCheckStatus === "pending") {
                return d
              }
              return {
                ...d,
                score,
                health_check_status: healthCheckStatus,
              }
            })
          : prev,
      )

      setActiveDomain((prev) => {
        if (!prev || prev.domain !== domain) return prev
        // Don't revert from completed (null) back to pending
        if (prev.health_check_status === null && healthCheckStatus === "pending") {
          return prev
        }
        return { ...prev, score, health_check_status: healthCheckStatus }
      })

      if (pollingRef.current?.domain === domain) {
        setIsPolling(false)
        pollingRef.current = null
      }
    },
    [],
  )

  const healthCheckCallbacks = useMemo(() => ({ received: handleHealthCheckReceived }), [handleHealthCheckReceived])

  // Subscribe to backend push notifications for completed health checks
  useActionCable({
    params: healthCheckChannelParams,
    callbacks: healthCheckCallbacks,
  })

  const skeletonColumns = [
    { label: i18n.t("js.email_accounts_center.domains_table.columns.domain") },
    { label: i18n.t("js.email_accounts_center.domains_table.columns.status") },
    {
      label: i18n.t("js.email_accounts_center.domains_table.columns.email_accounts"),
    },
    ...(isPremium
      ? [
          {
            label: i18n.t("js.email_accounts_center.domains_table.columns.health"),
          },
        ]
      : []),
    { label: i18n.t("js.email_accounts_center.domains_table.columns.type") },
  ]

  if (loading) {
    return (
      <div>
        <div className="main-heading">
          <h2 className="main-heading__count">
            <Skeleton size="freeform" style={{ width: "10.4rem", height: "1.6rem" }} />
          </h2>
        </div>

        <DataTableSkeleton
          columns={skeletonColumns}
          checkboxAriaLabel={i18n.t("js.email_accounts_center.domains_table.bulk.select_all")}
        />
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <div className="main-heading">
          <h2 className="main-heading__count">
            <span
              dangerouslySetInnerHTML={{
                __html: i18n.t("js.email_accounts_center.domains_table.count_html", {
                  pretty_count: i18n.numberToDelimited(total),
                  count: total,
                }),
              }}
            />
          </h2>

          <hr className="main-heading__separator" />

          <div className="main-heading__filtering">
            <SearchInput
              value={filters.searchQuery || ""}
              onChange={(value: string) => setFilters((prev) => ({ ...prev, searchQuery: value }))}
              placeholder={i18n.t("js.email_accounts_center.domains_table.search_placeholder")}
              ariaLabel={i18n.t("js.email_accounts_center.domains_table.search_aria")}
              clearAriaLabel={i18n.t("js.email_accounts_center.clear_search")}
            />
            <FiltersDropdown filters={filters} setFilters={setFilters} emailAccounts={emailAccounts} domains={[]} />
          </div>
        </div>

        <Alert variant="destructive" className="mb-3">
          <AlertIcon>
            <span className="h-alert__icon far fa-exclamation-circle"></span>
          </AlertIcon>
          <AlertContent>
            <AlertDescription>{i18n.t("js.email_accounts_center.domains_table.errors.alert")}</AlertDescription>
          </AlertContent>
        </Alert>

        <DataTableSkeleton
          columns={skeletonColumns}
          checkboxAriaLabel={i18n.t("js.email_accounts_center.domains_table.bulk.select_all")}
        />
      </div>
    )
  }

  if (!sectionedData) {
    return (
      <div>
        <div className="main-heading">
          <h2 className="main-heading__count">
            <span
              dangerouslySetInnerHTML={{
                __html: i18n.t("js.email_accounts_center.domains_table.count_html", {
                  pretty_count: i18n.numberToDelimited(total),
                  count: 0,
                }),
              }}
            />
          </h2>
        </div>
      </div>
    )
  }

  const totalCount = sectionedData.total || total
  const connectedCount = connectedItems.length
  const managedCount = managedItems.length
  const connectedActiveCount = (connectedItems as DomainDataWithId[]).filter((d) => d.status === "active").length
  const managedActiveCount = (managedItems as DomainDataWithId[]).filter((d) => d.status === "active").length

  return (
    <div>
      <div className="main-heading">
        <h2 className="main-heading__count">
          <span
            dangerouslySetInnerHTML={{
              __html: i18n.t("js.email_accounts_center.domains_table.count_html", {
                pretty_count: i18n.numberToDelimited(totalCount),
                count: totalCount,
              }),
            }}
          />
        </h2>

        <hr className="main-heading__separator" />

        <div className="main-heading__filtering">
          <SearchInput
            value={filters.searchQuery || ""}
            onChange={(value: string) => setFilters((prev) => ({ ...prev, searchQuery: value }))}
            placeholder={i18n.t("js.email_accounts_center.domains_table.search_placeholder")}
            ariaLabel={i18n.t("js.email_accounts_center.domains_table.search_aria")}
            clearAriaLabel={i18n.t("js.email_accounts_center.clear_search")}
          />
          <FiltersDropdown
            filters={filters}
            setFilters={setFilters}
            emailAccounts={emailAccounts}
            domains={rawData || []}
          />
        </div>
      </div>

      <FiltersTags filters={filters} setFilters={setFilters} emailAccounts={emailAccounts} domains={rawData || []} />

      <DataTable
        bulkActionsBar={
          <DataTableBulkActionsBar
            totalCount={totalCount}
            pageCount={allItems.length}
            selectedCount={selectedCount}
            onClearSelection={clearSelection}
            onSelectAll={() => selectAll(getNonPendingItems(allItems))}
            onSelectPage={() => selectAll(getNonPendingItems(allItems))}
            actions={bulkActions}
          />
        }
      >
        <DataTableHeader>
          <DataTableHeaderCell className="h-data-table__checkbox">
            <div className="h-data-table-cell h-data-table-cell--checkbox">
              <Checkbox
                aria-label={i18n.t("js.email_accounts_center.domains_table.bulk.select_all")}
                checked={isAllNonPendingSelected}
                onChange={() => selectAll(isAllNonPendingSelected ? [] : getNonPendingItems(allItems))}
              />
            </div>
          </DataTableHeaderCell>
          <DataTableHeaderCell sticky>
            {i18n.t("js.email_accounts_center.domains_table.columns.domain")}
          </DataTableHeaderCell>
          <DataTableHeaderCell>{i18n.t("js.email_accounts_center.domains_table.columns.status")}</DataTableHeaderCell>
          <DataTableHeaderCell>
            {i18n.t("js.email_accounts_center.domains_table.columns.email_accounts")}
          </DataTableHeaderCell>
          {isPremium && (
            <DataTableHeaderCell style={{ minWidth: "18rem", width: "18rem" }}>
              {i18n.t("js.email_accounts_center.domains_table.columns.health")}
            </DataTableHeaderCell>
          )}
          <DataTableHeaderCell>{i18n.t("js.email_accounts_center.domains_table.columns.type")}</DataTableHeaderCell>
          <DataTableHeaderCell></DataTableHeaderCell>
        </DataTableHeader>

        <DataTableBody>
          {/* Empty state when no results */}
          {totalCount === 0 && (
            <DataTableEmptyState
              colSpan={COLUMN_COUNT}
              onClearFilters={
                filters.searchQuery ||
                filters.emailAccountIds.length > 0 ||
                filters.status.length > 0 ||
                filters.healthFrom !== null ||
                filters.healthTo !== null ||
                filters.healthIsEmpty ||
                filters.type.length > 0
                  ? () =>
                      setFilters({
                        emailAccountIds: [],
                        status: [],
                        healthFrom: null,
                        healthTo: null,
                        healthIsEmpty: false,
                        type: [],
                        searchQuery: "",
                      })
                  : undefined
              }
            />
          )}

          {/* Connected domains group - only when both types exist */}
          {connectedCount > 0 && (
            <>
              {managedCount > 0 && (
                <DataTableGroupRow
                  colSpan={COLUMN_COUNT}
                  sectionId="connected"
                  checkboxCell={
                    <Checkbox
                      aria-label={i18n.t("js.email_accounts_center.domains_table.bulk.select_connected_group")}
                      checked={isConnectedSectionNonPendingSelected}
                      onChange={() => selectNonPendingSection("connected")}
                    />
                  }
                >
                  <strong className="fw-medium text-body-secondary mr-1">
                    {i18n.t("js.email_accounts_center.domains_table.groups.connected_domains")}
                  </strong>
                  <span className="text-xs text-hint">
                    · {connectedActiveCount}/{connectedCount}
                  </span>
                </DataTableGroupRow>
              )}

              {(connectedItems as DomainDataWithId[]).map((domain) => renderDomainRow(domain, "connected"))}
            </>
          )}

          {/* Managed domains group - only when both types exist */}
          {managedCount > 0 && (
            <>
              {connectedCount > 0 && (
                <DataTableGroupRow
                  colSpan={COLUMN_COUNT}
                  sectionId="managed"
                  checkboxCell={
                    <Checkbox
                      aria-label={i18n.t("js.email_accounts_center.domains_table.bulk.select_managed_group")}
                      checked={isManagedSectionNonPendingSelected}
                      onChange={() => selectNonPendingSection("managed")}
                    />
                  }
                >
                  <strong className="fw-medium text-body-secondary mr-1">
                    {i18n.t("js.email_accounts_center.domains_table.groups.managed_domains")}
                  </strong>
                  <span className="text-xs text-hint">
                    · {managedActiveCount}/{managedCount}
                  </span>
                  {allowEmailProvisioning && (
                    <Button size="xs" className="ml-auto" type="button" asChild>
                      <a href={email_accounts_domains_path()} data-turbo="false">
                        <Icon icon="plus" className="mr-1" aria-hidden="true" />
                        {i18n.t("js.email_accounts_center.groups.create_domains")}
                      </a>
                    </Button>
                  )}
                </DataTableGroupRow>
              )}

              {(managedItems as DomainDataWithId[]).map((domain) => renderDomainRow(domain, "managed"))}
            </>
          )}
        </DataTableBody>
      </DataTable>
      <Sheet
        open={!!activeDomain}
        modal={false}
        onOpenChange={(open) => {
          if (!open) {
            setActiveDomain(null)
            if (onDomainIdChange) {
              onDomainIdChange(null)
            }
          }
        }}
      >
        <SheetContent side="right" size="md" className="p-0">
          {activeDomain && (
            <DomainSidepanel
              domain={activeDomain}
              allowEmailProvisioning={allowEmailProvisioning}
              isPremium={isPremium}
              onHealthCheckTriggered={() => {
                pollingRef.current = {
                  domain: activeDomain.domain,
                  initialScore: activeDomain.score ?? null,
                  pollCount: 0,
                }
                setIsPolling(true)
              }}
              onDomainUpdated={(updatedDomain) => {
                // Update the domain in the list
                if (rawData) {
                  const updatedData = rawData.map((d) => (d.domain === updatedDomain.domain ? updatedDomain : d))
                  setRawData(updatedData)
                  setActiveDomain(updatedDomain)
                }
              }}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Confirmation Dialogs */}
      {confirmBulkCancelRenewalOpen && (
        <Dialog ref={bulkCancelRenewalDialogRef}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {i18n.t("js.email_accounts_center.domains_table.bulk.cancel_renewal_title", {
                  count: selectedIds.length,
                  pretty_count: i18n.numberToDelimited(selectedIds.length),
                })}
              </DialogTitle>
              <DialogCloseButton onClick={() => setConfirmBulkCancelRenewalOpen(false)} />
            </DialogHeader>
            <DialogBody>
              <p
                dangerouslySetInnerHTML={{
                  __html: i18n.t("js.email_accounts_center.domains_table.bulk.cancel_renewal_confirm_html", {
                    count: selectedIds.length,
                  }),
                }}
              />
            </DialogBody>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConfirmBulkCancelRenewalOpen(false)}>
                {i18n.t("js.email_accounts_center.domains_table.bulk.cancel_renewal_keep")}
              </Button>
              <Button
                variant="danger"
                onClick={async () => {
                  setConfirmBulkCancelRenewalOpen(false)
                  await executeBulkCancelRenewal()
                }}
              >
                {i18n.t("js.email_accounts_center.domains_table.bulk.cancel_renewal_cta")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {confirmIndividualCancelRenewalOpen && pendingDomain && (
        <Dialog ref={individualCancelRenewalDialogRef}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{i18n.t("js.email_accounts_center.domain.cancel_renewal_title")}</DialogTitle>
              <DialogCloseButton onClick={() => setConfirmIndividualCancelRenewalOpen(false)} />
            </DialogHeader>
            <DialogBody>
              <p
                dangerouslySetInnerHTML={{
                  __html: pendingDomain.expire_at
                    ? i18n.t("js.email_accounts_center.domain.cancel_renewal_confirm_html", {
                        expiration_date: formatDate(pendingDomain.expire_at),
                      })
                    : i18n.t("js.email_accounts_center.domain.cancel_renewal_confirm_no_date_html"),
                }}
              />
            </DialogBody>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConfirmIndividualCancelRenewalOpen(false)}>
                {i18n.t("js.email_accounts_center.domain.cancel_renewal_keep")}
              </Button>
              <Button
                variant="danger"
                onClick={async () => {
                  setConfirmIndividualCancelRenewalOpen(false)
                  if (pendingDomain) {
                    await executeIndividualCancelRenewal(pendingDomain)
                  }
                }}
              >
                {i18n.t("js.email_accounts_center.domain.cancel_renewal_cta")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
